import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogDescription,
	DialogHeader,
	DialogFooter,
	DialogClose,
} from '@/components/ui/dialog';
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSlot,
} from '@/components/ui/input-otp';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@/lib/utils';
import { Controller, useForm } from 'react-hook-form';
import ArrowReloadHorizontalIcon from '@/components/icons/ArrowReloadHorizontalIcon';
import InformationCircle from '@/components/icons/InformationCircle';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';

type PinDto = {
	pin: string;
};

const VerifyFormSchema = z.object({
	pin: z.string().length(4, {
		message: '4-digit code is needed.',
	}),
});

type VerifyFormData = z.infer<typeof VerifyFormSchema>;

type Props = {
	onClose: () => void;
	isMobile?: boolean;
};

const EVerifyEmailModal: FC<PropsWithChildren<Props>> = ({
	onClose,
	isMobile,
}) => {
	const [isOpen, setIsOpen] = useState(true);
	const [resendTimer, setResendTimer] = useState(60);
	const { t } = useTranslation();

	useEffect(() => {
		if (resendTimer > 0) {
			const timer = setInterval(() => {
				setResendTimer((prev) => prev - 1);
			}, 1000);
			return () => clearInterval(timer);
		}
	}, [resendTimer]);

	const { control, handleSubmit } = useForm<VerifyFormData>({
		resolver: zodResolver(VerifyFormSchema),
		defaultValues: {
			pin: '',
		},
	});

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	const handleResendCode = () => {
		if (resendTimer === 0) {
			setResendTimer(60);
		}
	};

	const submitFormHandler = async (form: VerifyFormData) => {
		const dto: PinDto = {
			pin: form.pin,
		};
		onClose();
	};

	return (
		<Dialog
			open={isOpen}
			onOpenChange={handleClose}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{t('Verify email')}
						<DialogClose
							className={cn(
								'ml-auto flex items-center justify-center cursor-pointer',
								isMobile &&
									'size-11 border border-popover-100 rounded-full',
							)}>
							<Cancel_01_Icon />
						</DialogClose>
					</DialogTitle>
				</DialogHeader>
				<div className='flex flex-col gap-4'>
					<DialogDescription>
						To turn on 2FA, first verify your email by entering the
						4-digit code we sent to janecooper@mail.com
					</DialogDescription>
					<Controller
						name='pin'
						control={control}
						render={({
							field: { value, onChange },
							fieldState,
							formState,
						}) => (
							<>
								<InputOTP
									maxLength={4}
									value={value}
									onChange={onChange}
									disabled={formState.disabled}>
									{Array.from({ length: 4 }).map(
										(_, index) => (
											<InputOTPGroup key={index}>
												<InputOTPSlot index={index} />
											</InputOTPGroup>
										),
									)}
								</InputOTP>
								{fieldState.invalid && (
									<div className='flex items-center gap-1.5'>
										<InformationCircle className='size-4 stroke-error-1000' />
										<span className='text-xs text-error-1000'>
											{fieldState.error?.message}
										</span>
									</div>
								)}
							</>
						)}
					/>
				</div>
				<DialogFooter
					className={cn('flex-col-reverse', isMobile && ' mt-auto')}>
					<Button
						variant='outline'
						onClick={handleResendCode}
						disabled={resendTimer > 0}>
						<ArrowReloadHorizontalIcon
							className={cn(
								resendTimer > 0 ? 'stroke-popover-400' : '',
							)}
						/>
						{resendTimer > 0
							? t(`Resend code in ${resendTimer}s`)
							: t('Resend code')}
					</Button>
					<Button onClick={handleSubmit(submitFormHandler)}>
						{t('Verify')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default EVerifyEmailModal;
